import { useEffect, useMemo, useState } from 'react';
import type { Benutzer, Mitglied } from '@beholder/shared';
import {
  KURS_QUELLE_NAMEN,
  PROJEKT_WAEHRUNG,
  STEUER_SAETZE,
  betrag,
  centAlsEingabe,
  centAusText,
  heute,
  kursAusTabelle,
  kursNormal,
  kursText,
  satzAusText,
  satzText,
  statusListe,
  statusName,
  steuerBrutto,
  steuerErgaenzen,
  umrechnen,
  type BelegAnsicht,
  type BelegArt,
  type BelegStatus,
  type BudgetStand,
  type SteuerPosten,
} from '@beholder/shared/module/budget';
import { api } from '../../core/api';
import { Anlagen } from '../../core/Anlagen';
import { Symbol } from '../../core/symbole';
import { Auswahl, Dialog, Eingabe, Feld, Knopf, Meldung, Textfeld, datum, datumZeit, useAktion } from '../../core/ui';
import { Euro } from './Euro';
import { LabelEingabe, LabelMarken } from './Labels';
import { pfad, useKurse, type MindmapKnoten } from './hilfe';
import { WaehrungsAuswahl } from './Rechner';

const HERKUNFT: Record<BelegAnsicht['quelleArt'], string> = { mail: 'aus einer Mail', ki: 'von der KI-Hilfe', hand: 'von Hand' };
const W = PROJEKT_WAEHRUNG;

export const ART_NAME: Record<BelegArt, string> = { rechnung: 'Rechnung', angebot: 'Angebot', einnahme: 'Einnahme' };
const ART_HINWEIS: Record<BelegArt, string> = {
  rechnung: 'tatsächliche Kosten',
  angebot: 'Kostenvorschlag – noch keine Ausgabe',
  einnahme: 'Zuschuss, Sponsoring, Verkauf …',
};
/** „Ausgegeben von“ je Art. */
export const WER_NAME: Record<BelegArt, string> = { rechnung: 'Ausgegeben von', angebot: 'Angefragt von', einnahme: 'Eingeworben / kassiert von' };

// ------------------------------------------------------------------ Mehrwertsteuer-Zeilen im Formular

type SteuerFeld = 'satz' | 'netto' | 'steuer' | 'brutto';
interface SteuerZeile {
  satz: string;
  netto: string;
  steuer: string;
  brutto: string;
  /** Die zwei zuletzt eingetippten Felder – daraus wird der Rest gerechnet. */
  zuletzt: SteuerFeld[];
}

const satzEingabe = (bp: number) => satzText(bp).replace(' %', '');

function zeileAus(p: SteuerPosten): SteuerZeile {
  return {
    satz: satzEingabe(p.satzBp),
    netto: centAlsEingabe(p.nettoCent),
    steuer: centAlsEingabe(p.steuerCent),
    brutto: centAlsEingabe(p.nettoCent + p.steuerCent),
    zuletzt: ['netto', 'steuer'],
  };
}

/** Feld geändert → aus den zwei zuletzt eingegebenen Werten die anderen beiden rechnen. */
function zeileAendern(z: SteuerZeile, feld: SteuerFeld, wert: string): SteuerZeile {
  const roh: SteuerZeile = { ...z, [feld]: wert };
  // Nur gültig ausgefüllte Felder zählen als „eingegeben“ (ein geleertes Feld wird wieder gerechnet)
  const gueltig = (f: SteuerFeld) => (f === 'satz' ? satzAusText(roh.satz) !== null : centAusText(roh[f]) !== null);
  const neu: SteuerZeile = { ...roh, zuletzt: [feld, ...z.zuletzt.filter((x) => x !== feld)].filter(gueltig).slice(0, 2) };
  const werte = {
    satzBp: neu.zuletzt.includes('satz') ? satzAusText(neu.satz) : null,
    nettoCent: neu.zuletzt.includes('netto') ? centAusText(neu.netto) : null,
    steuerCent: neu.zuletzt.includes('steuer') ? centAusText(neu.steuer) : null,
    bruttoCent: neu.zuletzt.includes('brutto') ? centAusText(neu.brutto) : null,
  };
  const p = steuerErgaenzen(werte);
  if (!p) return neu;
  return {
    ...neu,
    satz: neu.zuletzt.includes('satz') ? neu.satz : satzEingabe(p.satzBp),
    netto: neu.zuletzt.includes('netto') ? neu.netto : centAlsEingabe(p.nettoCent),
    steuer: neu.zuletzt.includes('steuer') ? neu.steuer : centAlsEingabe(p.steuerCent),
    brutto: neu.zuletzt.includes('brutto') ? neu.brutto : centAlsEingabe(p.nettoCent + p.steuerCent),
  };
}

function postenAus(z: SteuerZeile): SteuerPosten | null {
  const satzBp = satzAusText(z.satz);
  const nettoCent = centAusText(z.netto);
  const steuerCent = centAusText(z.steuer);
  const bruttoCent = centAusText(z.brutto);
  if (satzBp === null || nettoCent === null || steuerCent === null || bruttoCent === null) return null;
  if (nettoCent + steuerCent !== bruttoCent) return null;
  return { satzBp, nettoCent, steuerCent };
}

function neueZeile(bruttoText: string, satzBp = 1900): SteuerZeile {
  return zeileAendern({ satz: satzEingabe(satzBp), netto: '', steuer: '', brutto: '', zuletzt: ['satz'] }, 'brutto', bruttoText);
}

// ------------------------------------------------------------------ Formular

interface Formular {
  art: BelegArt;
  titel: string;
  betrag: string;
  waehrung: string;
  datum: string;
  absender: string;
  kategorieId: string;
  labels: string[];
  status: BelegStatus;
  /** Mitglieds-ID, '' = niemand, '__frei' = freier Name */
  wer: string;
  frei: string;
  mindmapKnotenId: string;
  notiz: string;
  kursManuell: string;
  steuerAn: boolean;
  steuer: SteuerZeile[];
}

function formularAus(b: BelegAnsicht | null, art: BelegArt, ich: Benutzer, mitglieder: Mitglied[] | null): Formular {
  if (b) {
    return {
      art: b.art,
      titel: b.titel,
      betrag: centAlsEingabe(b.betragCent),
      waehrung: b.waehrung,
      datum: b.datum ?? '',
      absender: b.absender,
      kategorieId: b.kategorieId ?? '',
      labels: b.labels,
      status: b.status,
      wer: b.teilnehmerId ?? (b.teilnehmerFrei ? '__frei' : ''),
      frei: b.teilnehmerFrei ?? '',
      mindmapKnotenId: b.mindmapKnotenId ?? '',
      notiz: b.notiz,
      kursManuell: b.kursQuelle === 'manuell' && b.kurs ? b.kurs.replace('.', ',') : '',
      steuerAn: b.steuer.length > 0,
      steuer: b.steuer.map(zeileAus),
    };
  }
  return {
    art,
    titel: '',
    betrag: '',
    waehrung: W,
    datum: heute(),
    absender: '',
    kategorieId: '',
    labels: [],
    status: 'offen',
    wer: mitglieder?.some((m) => m.benutzerId === ich.id) ? ich.id : '',
    frei: '',
    mindmapKnotenId: '',
    notiz: '',
    kursManuell: '',
    steuerAn: false,
    steuer: [],
  };
}

export type DialogArt = { art: 'ansicht'; id: string } | { art: 'neu'; belegArt: BelegArt } | { art: 'vorschlag'; id: string };

export function BelegDialog({
  projektId,
  s,
  modus,
  ich,
  mitglieder,
  knoten,
  darfSchreiben,
  darfLoeschen,
  schliessen,
  oeffnen,
  fertig,
}: {
  projektId: string;
  s: BudgetStand;
  modus: DialogArt;
  ich: Benutzer;
  mitglieder: Mitglied[] | null;
  knoten: MindmapKnoten[];
  darfSchreiben: boolean;
  /** Nur die Projektleitung darf Belege löschen (in den Papierkorb). */
  darfLoeschen: boolean;
  schliessen(): void;
  oeffnen(m: DialogArt): void;
  fertig(): void | Promise<void>;
}) {
  const alle = useMemo(() => [...s.belege, ...s.vorschlaege], [s]);
  const beleg = modus.art === 'neu' ? null : (alle.find((b) => b.id === modus.id) ?? null);
  const [bearbeiten, setBearbeiten] = useState(modus.art !== 'ansicht');
  const [f, setF] = useState<Formular>(() => formularAus(beleg, modus.art === 'neu' ? modus.belegArt : 'rechnung', ich, mitglieder));
  const [startVersion, setStartVersion] = useState(beleg?.version ?? 0);
  const a = useAktion();

  // Vorschlag-/Neu-Modus: Formular sofort offen; Ansicht: erst Ansehen
  useEffect(() => {
    setBearbeiten(modus.art !== 'ansicht');
    setF(formularAus(beleg, modus.art === 'neu' ? modus.belegArt : 'rechnung', ich, mitglieder));
    setStartVersion(beleg?.version ?? 0);
    a.setFehler('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modus.art, modus.art === 'neu' ? modus.belegArt : modus.id]);

  useEffect(() => {
    if (modus.art === 'neu' && mitglieder?.some((m) => m.benutzerId === ich.id)) {
      setF((x) => (x.wer ? x : { ...x, wer: ich.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mitglieder]);

  if (modus.art !== 'neu' && !beleg) {
    return (
      <Dialog titel="Beleg" offen schliessen={schliessen}>
        <Meldung art="info">Dieser Beleg ist nicht mehr da – vielleicht liegt er inzwischen im Papierkorb oder wurde verworfen.</Meldung>
      </Dialog>
    );
  }

  const fremdGeaendert = !!beleg && bearbeiten && beleg.version !== startVersion;
  const posten = f.steuerAn ? f.steuer.map(postenAus) : [];
  const postenOk = posten.every((p): p is SteuerPosten => p !== null);
  const cent = f.steuerAn ? (postenOk && posten.length ? steuerBrutto(posten as SteuerPosten[]) : null) : centAusText(f.betrag);
  const kursHand = f.kursManuell.trim() ? kursNormal(f.kursManuell) : null;
  const fehlerFeld = !f.titel.trim()
    ? 'Bitte einen Titel eingeben.'
    : f.steuerAn && (!posten.length || !postenOk)
      ? 'Bitte bei jedem Steuerposten zwei Werte eingeben (z. B. Satz und Brutto) – der Rest wird gerechnet.'
      : cent === null
        ? 'Bitte einen gültigen Betrag eingeben, z. B. 1.234,50'
        : f.kursManuell.trim() && !kursHand
          ? 'Der Kurs muss eine positive Zahl sein, z. B. 0,9234'
          : f.wer === '__frei' && !f.frei.trim()
            ? 'Bitte den Namen eintragen oder „niemand“ wählen.'
            : '';
  const set = <K extends keyof Formular>(k: K, v: Formular[K]) => setF((x) => ({ ...x, [k]: v }));

  function body() {
    const frei = f.wer === '__frei';
    return {
      art: f.art,
      titel: f.titel.trim(),
      betragCent: cent,
      waehrung: f.waehrung,
      datum: f.datum || null,
      absender: f.absender.trim(),
      kategorieId: f.art === 'einnahme' ? null : f.kategorieId || null,
      labels: f.art === 'einnahme' ? f.labels : [],
      status: f.status,
      teilnehmerId: frei ? null : f.wer || null,
      teilnehmerFrei: frei ? f.frei.trim() : null,
      mindmapKnotenId: f.mindmapKnotenId || null,
      notiz: f.notiz,
      kursManuell: f.waehrung === W ? null : kursHand,
      steuer: f.steuerAn ? posten : [],
    };
  }

  async function speichern() {
    if (fehlerFeld) return a.setFehler(fehlerFeld);
    if (modus.art === 'neu') {
      const r = await a.ausfuehren(() => api.post<BelegAnsicht>(pfad(projektId, '/belege'), body()));
      if (r) {
        await fertig();
        oeffnen({ art: 'ansicht', id: r.id });
      }
    } else if (modus.art === 'vorschlag') {
      const r = await a.ausfuehren(() => api.post<BelegAnsicht>(pfad(projektId, `/belege/${beleg!.id}/uebernehmen`), { ...body(), version: startVersion }));
      if (r) {
        fertig();
        schliessen();
      }
    } else {
      const r = await a.ausfuehren(() => api.patch<BelegAnsicht>(pfad(projektId, `/belege/${beleg!.id}`), { ...body(), version: startVersion }));
      if (r) {
        fertig();
        setBearbeiten(false);
      }
    }
  }

  async function aktion(rest: string, frage?: string, nachher?: (r: BelegAnsicht) => void) {
    if (frage && !confirm(frage)) return;
    const r = await a.ausfuehren(() => (rest === 'loeschen' ? api.delete(pfad(projektId, `/belege/${beleg!.id}`)) : api.post<BelegAnsicht>(pfad(projektId, `/belege/${beleg!.id}/${rest}`))));
    if (r) {
      await fertig();
      if (nachher) nachher(r as BelegAnsicht);
      else schliessen();
    }
  }

  async function status(neu: BelegStatus) {
    const r = await a.ausfuehren(() => api.patch(pfad(projektId, `/belege/${beleg!.id}`), { status: neu, version: beleg!.version }));
    if (r) fertig();
  }

  const titel =
    modus.art === 'neu'
      ? modus.belegArt === 'angebot'
        ? 'Neues Angebot'
        : `Neue ${ART_NAME[modus.belegArt]}`
      : modus.art === 'vorschlag'
        ? `Vorschlag: ${beleg!.titel}`
        : `${ART_NAME[beleg!.art]}: ${beleg!.titel}`;

  const fuss = bearbeiten ? (
    <>
      <Knopf art="haupt" onClick={speichern} disabled={a.laeuft}>
        {a.laeuft ? 'Speichert …' : modus.art === 'vorschlag' ? '✓ Übernehmen' : 'Speichern'}
      </Knopf>
      {modus.art === 'vorschlag' ? (
        <Knopf art="gefahr" onClick={() => aktion('verwerfen', `Vorschlag „${beleg!.titel}“ verwerfen?\n\nEr liegt dann 180 Tage im Papierkorb und lässt sich dort zurückholen.`)} disabled={a.laeuft}>
          Verwerfen
        </Knopf>
      ) : (
        <Knopf onClick={() => (modus.art === 'neu' ? schliessen() : (setBearbeiten(false), setF(formularAus(beleg, 'rechnung', ich, mitglieder))))}>Abbrechen</Knopf>
      )}
    </>
  ) : darfSchreiben && beleg && !beleg.vorschlag ? (
    <>
      <Knopf art="haupt" onClick={() => (setStartVersion(beleg.version), setF(formularAus(beleg, 'rechnung', ich, mitglieder)), setBearbeiten(true))}>
        ✎ Bearbeiten
      </Knopf>
      {beleg.art === 'rechnung' && beleg.status === 'offen' && <Knopf onClick={() => status('bezahlt')}>✓ Als bezahlt markieren</Knopf>}
      {beleg.art === 'rechnung' && beleg.status === 'bezahlt' && <Knopf onClick={() => status('offen')}>Wieder offen</Knopf>}
      {beleg.art === 'einnahme' && beleg.status === 'offen' && <Knopf onClick={() => status('erhalten')}>✓ Als erhalten markieren</Knopf>}
      {beleg.art === 'einnahme' && beleg.status === 'erhalten' && <Knopf onClick={() => status('offen')}>Wieder erwartet</Knopf>}
      {beleg.art === 'angebot' && beleg.status === 'offen' && (
        <>
          <Knopf
            onClick={() =>
              aktion('umwandeln', `Angebot „${beleg.titel}“ in eine Rechnung umwandeln? Die Rechnung wird mit dem heutigen Datum angelegt und lässt sich danach anpassen.`, (r) =>
                oeffnen({ art: 'ansicht', id: r.id }),
              )
            }
          >
            → In Rechnung umwandeln
          </Knopf>
          <Knopf onClick={() => status('abgelehnt')}>Ablehnen</Knopf>
        </>
      )}
      {beleg.art === 'angebot' && beleg.status === 'abgelehnt' && <Knopf onClick={() => status('offen')}>Wieder offen</Knopf>}
      {darfLoeschen && (
        <>
          <span className="abstand" />
          <Knopf
            art="gefahr"
            onClick={() => aktion('loeschen', `${ART_NAME[beleg.art]} „${beleg.titel}“ löschen?\n\nSie liegt mit allen Anlagen 180 Tage im Papierkorb und lässt sich dort zurückholen.`)}
          >
            {Symbol.muell} Löschen
          </Knopf>
        </>
      )}
    </>
  ) : undefined;

  return (
    <Dialog titel={titel} offen breit schliessen={schliessen} fuss={fuss}>
      <div className="stapel">
        <Meldung>{a.fehler}</Meldung>
        {fremdGeaendert && (
          <Meldung art="info">
            Jemand anderes hat diesen Beleg gerade geändert.{' '}
            <button
              type="button"
              className="bu-link"
              onClick={() => {
                setF(formularAus(beleg, 'rechnung', ich, mitglieder));
                setStartVersion(beleg!.version);
              }}
            >
              Neue Fassung laden
            </button>{' '}
            (deine Eingaben gehen dabei verloren).
          </Meldung>
        )}
        {modus.art === 'vorschlag' && beleg && (
          <Meldung art="info">
            Vorgeschlagen {HERKUNFT[beleg.quelleArt]} am {datumZeit(beleg.erstelltAm)}.
            {beleg.kategorieVorschlag && !beleg.kategorieId && <> Vorgeschlagene Kategorie: „{beleg.kategorieVorschlag}“ (nicht gefunden).</>} Bitte prüfen und dann übernehmen oder
            verwerfen.
          </Meldung>
        )}
        {bearbeiten ? (
          <BelegFormular projektId={projektId} s={s} f={f} set={set} setF={setF} mitglieder={mitglieder} knoten={knoten} neu={modus.art === 'neu'} cent={cent} kursHand={kursHand} />
        ) : (
          beleg && <BelegAnsichtInhalt projektId={projektId} b={beleg} s={s} knoten={knoten} oeffnen={oeffnen} />
        )}
        {beleg && (
          <section>
            <h3 className="bu-abschnitt">Anlagen</h3>
            {/* key: lädt die Liste neu, wenn jemand anderes eine Anlage hochlädt/löscht (Live-Hinweis __anlagen) */}
            <Anlagen key={beleg.anzahlAnlagen} projektId={projektId} bereich="budget" bezug={beleg.id} darfSchreiben={darfSchreiben} beiAenderung={() => fertig()} />
          </section>
        )}
        {modus.art === 'neu' && <p className="leise klein">Anlagen (z. B. die Rechnung als PDF) kannst du nach dem Speichern anhängen.</p>}
      </div>
    </Dialog>
  );
}

function BelegFormular({
  projektId,
  s,
  f,
  set,
  setF,
  mitglieder,
  knoten,
  neu,
  cent,
  kursHand,
}: {
  projektId: string;
  s: BudgetStand;
  f: Formular;
  set<K extends keyof Formular>(k: K, v: Formular[K]): void;
  setF(fn: (x: Formular) => Formular): void;
  mitglieder: Mitglied[] | null;
  knoten: MindmapKnoten[];
  neu: boolean;
  cent: number | null;
  kursHand: string | null;
}) {
  const tag = f.datum && f.datum < heute() ? f.datum : null;
  const { kurse } = useKurse(projektId, tag);
  const autoKurs = kurse ? kursAusTabelle(kurse.jeEuro, f.waehrung, W) : null;
  const kurs = kursHand ?? autoKurs;
  const stati = statusListe(f.art).filter((x) => x !== 'umgewandelt');
  const [kursOffen, setKursOffen] = useState(!!f.kursManuell);
  const einnahme = f.art === 'einnahme';

  const artWaehlen = (art: BelegArt) =>
    setF((x) => ({ ...x, art, status: (statusListe(art) as readonly string[]).includes(x.status) && x.status !== 'umgewandelt' ? x.status : 'offen' }));
  const steuerSchalten = (an: boolean) =>
    setF((x) => ({ ...x, steuerAn: an, steuer: an && !x.steuer.length ? [neueZeile(x.betrag)] : x.steuer, betrag: !an && cent !== null ? centAlsEingabe(cent) : x.betrag }));
  const zeileSetzen = (i: number, feld: SteuerFeld, wert: string) =>
    setF((x) => ({ ...x, steuer: x.steuer.map((z, j) => (j === i ? zeileAendern(z, feld, wert) : z)) }));

  return (
    <div className="bu-formular">
      <div className="bu-art-wahl bu-voll" role="radiogroup" aria-label="Art des Belegs">
        {(['rechnung', 'angebot', 'einnahme'] as const).map((art) => (
          <button key={art} type="button" role="radio" aria-checked={f.art === art} className={`bu-art${f.art === art ? ' aktiv' : ''}`} onClick={() => artWaehlen(art)}>
            <strong>{ART_NAME[art]}</strong>
            <span className="leise klein">{ART_HINWEIS[art]}</span>
          </button>
        ))}
      </div>
      <Feld titel="Titel">
        <Eingabe autoFocus={neu} value={f.titel} onChange={(e) => set('titel', e.target.value)} maxLength={300} placeholder={einnahme ? 'z. B. Kulturförderung der Stadt' : 'z. B. Scheinwerfer für die Bühne'} />
      </Feld>
      <Feld titel={einnahme ? 'Von (Geldgeber, Sponsor, Käufer)' : f.art === 'rechnung' ? 'Von (Firma oder Person)' : 'Angebot von (Firma oder Person)'}>
        <Eingabe value={f.absender} onChange={(e) => set('absender', e.target.value)} maxLength={300} placeholder={einnahme ? 'z. B. Kulturamt' : 'z. B. Licht-Müller GmbH'} />
      </Feld>
      <div className="bu-zwei">
        <Feld titel={f.steuerAn ? 'Betrag brutto (aus den Posten)' : 'Betrag (brutto)'}>
          <Eingabe
            inputMode="decimal"
            value={f.steuerAn ? (cent === null ? '' : centAlsEingabe(cent)) : f.betrag}
            onChange={(e) => set('betrag', e.target.value)}
            readOnly={f.steuerAn}
            placeholder="1.234,50"
            aria-invalid={!f.steuerAn && !!f.betrag && cent === null}
          />
        </Feld>
        <Feld titel="Währung">
          <WaehrungsAuswahl wert={f.waehrung} aendern={(v) => set('waehrung', v)} />
        </Feld>
      </div>
      <Feld titel={f.art === 'rechnung' ? 'Rechnungsdatum' : f.art === 'angebot' ? 'Datum des Angebots' : 'Datum (Zusage oder Eingang)'}>
        <Eingabe type="date" value={f.datum} onChange={(e) => set('datum', e.target.value)} />
      </Feld>
      {f.waehrung !== W && (
        <div className="bu-voll bu-umrechnung-vorschau">
          {cent !== null && kurs ? (
            <div>
              ≈ <strong>{betrag(umrechnen(cent, kurs), W)}</strong>{' '}
              <span className="leise klein">
                ({kursText(kurs, f.waehrung, W)}
                {kursHand ? ', von Hand' : kurse?.datum ? `, EZB-Kurs vom ${datum(kurse.datum)}` : ''}
                {!kursHand && kurse?.stand === 'ersatz' ? ' – Kursquelle nicht erreichbar, letzter bekannter Kurs' : ''})
              </span>
            </div>
          ) : cent !== null && kurse && !autoKurs ? (
            <span className="bu-text-warn">Für {f.waehrung} gibt es keinen EZB-Kurs – bitte den Kurs unten von Hand eintragen.</span>
          ) : (
            <span className="leise">Wird beim Speichern mit dem Kurs vom Belegdatum in Euro umgerechnet.</span>
          )}
          {!kursOffen ? (
            <button type="button" className="bu-link klein" onClick={() => setKursOffen(true)}>
              Kurs selbst eintragen
            </button>
          ) : (
            <Feld titel={`Kurs von Hand: 1 ${f.waehrung} = … EUR`} hinweis="Leer lassen für den EZB-Kurs vom Belegdatum.">
              <Eingabe inputMode="decimal" value={f.kursManuell} onChange={(e) => set('kursManuell', e.target.value)} placeholder={autoKurs?.replace('.', ',') ?? 'z. B. 0,9234'} />
            </Feld>
          )}
        </div>
      )}

      <div className="bu-voll bu-steuer">
        <label className="schalter">
          <input type="checkbox" checked={f.steuerAn} onChange={(e) => steuerSchalten(e.target.checked)} />
          <span>
            Mehrwertsteuer aufschlüsseln
            <span className="leise klein"> – zwei Werte je Zeile eingeben (z. B. Satz und Brutto), der Rest wird gerechnet</span>
          </span>
        </label>
        {f.steuerAn && (
          <div className="bu-steuer-tabelle" role="table" aria-label="Steuerposten">
            <div className="bu-steuer-kopf" role="row">
              <span role="columnheader">Satz %</span>
              <span role="columnheader">Netto</span>
              <span role="columnheader">Steuer</span>
              <span role="columnheader">Brutto</span>
              <span />
            </div>
            {f.steuer.map((z, i) => {
              const ok = postenAus(z) !== null;
              return (
                <div key={i} className={`bu-steuer-zeile${ok ? '' : ' unfertig'}`} role="row">
                  <Eingabe inputMode="decimal" list="bu-saetze" value={z.satz} onChange={(e) => zeileSetzen(i, 'satz', e.target.value)} aria-label="Steuersatz in Prozent" />
                  <Eingabe inputMode="decimal" value={z.netto} onChange={(e) => zeileSetzen(i, 'netto', e.target.value)} aria-label="Netto" />
                  <Eingabe inputMode="decimal" value={z.steuer} onChange={(e) => zeileSetzen(i, 'steuer', e.target.value)} aria-label="Steuerbetrag" />
                  <Eingabe inputMode="decimal" value={z.brutto} onChange={(e) => zeileSetzen(i, 'brutto', e.target.value)} aria-label="Brutto" />
                  <Knopf
                    klein
                    art="leise"
                    aria-label="Posten entfernen"
                    title="Posten entfernen"
                    onClick={() => setF((x) => ({ ...x, steuer: x.steuer.filter((_, j) => j !== i), steuerAn: x.steuer.length > 1 }))}
                  >
                    ✕
                  </Knopf>
                </div>
              );
            })}
            <datalist id="bu-saetze">
              {STEUER_SAETZE.map((bp) => (
                <option key={bp} value={satzEingabe(bp)} />
              ))}
            </datalist>
            <button type="button" className="bu-link klein" onClick={() => setF((x) => ({ ...x, steuer: [...x.steuer, neueZeile('', 700)] }))}>
              + weiterer Steuersatz
            </button>
          </div>
        )}
      </div>

      {einnahme ? (
        <Feld titel="Labels" hinweis="Frei wählbar, mehrere möglich – z. B. Zuschuss, Stadt, Getränke. Zählen nicht gegen Kategorie-Budgets.">
          <LabelEingabe wert={f.labels} aendern={(l) => set('labels', l)} bekannt={s.labels} />
        </Feld>
      ) : (
        <Feld titel="Kategorie">
          <Auswahl value={f.kategorieId} onChange={(e) => set('kategorieId', e.target.value)}>
            <option value="">– ohne Kategorie –</option>
            {s.kategorien.map((k) => (
              <option key={k.id} value={k.id}>
                {'  '.repeat(k.tiefe)}
                {k.name}
              </option>
            ))}
          </Auswahl>
        </Feld>
      )}
      <Feld titel="Status">
        <Auswahl value={f.status} onChange={(e) => set('status', e.target.value as BelegStatus)} disabled={f.status === 'umgewandelt'}>
          {(f.status === 'umgewandelt' ? (['umgewandelt'] as BelegStatus[]) : stati).map((st) => (
            <option key={st} value={st}>
              {statusName(f.art, st)}
            </option>
          ))}
        </Auswahl>
      </Feld>
      <Feld titel={WER_NAME[f.art]}>
        <Auswahl value={f.wer} onChange={(e) => set('wer', e.target.value)}>
          <option value="">– niemand zugeordnet –</option>
          {mitglieder?.map((m) => (
            <option key={m.benutzerId} value={m.benutzerId}>
              {m.anzeigename}
            </option>
          ))}
          <option value="__frei">Anderer Name (ohne Konto) …</option>
        </Auswahl>
      </Feld>
      {f.wer === '__frei' && (
        <Feld titel="Name" hinweis="Für Helferinnen und Helfer ohne Beholder-Konto.">
          <Eingabe list="bu-freie-namen" value={f.frei} onChange={(e) => set('frei', e.target.value)} maxLength={120} placeholder="z. B. Oma Erna" autoFocus />
          <datalist id="bu-freie-namen">
            {s.freieNamen.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
        </Feld>
      )}
      {knoten.length > 0 && (
        <Feld titel="Mindmap-Punkt (optional)">
          <Auswahl value={f.mindmapKnotenId} onChange={(e) => set('mindmapKnotenId', e.target.value)}>
            <option value="">– keiner –</option>
            {knoten.map((k) => (
              <option key={k.id} value={k.id}>
                {k.pfad}
              </option>
            ))}
          </Auswahl>
        </Feld>
      )}
      <Feld titel="Notiz">
        <Textfeld rows={2} value={f.notiz} onChange={(e) => set('notiz', e.target.value)} maxLength={5000} />
      </Feld>
    </div>
  );
}

function BelegAnsichtInhalt({
  projektId,
  b,
  s,
  knoten,
  oeffnen,
}: {
  projektId: string;
  b: BelegAnsicht;
  s: BudgetStand;
  knoten: MindmapKnoten[];
  oeffnen(m: DialogArt): void;
}) {
  const fremd = b.waehrung !== W;
  const { kurse } = useKurse(projektId, null);
  const heuteKurs = fremd && kurse ? kursAusTabelle(kurse.jeEuro, b.waehrung, W) : null;
  const knotenPfad = b.mindmapKnotenId ? (knoten.find((k) => k.id === b.mindmapKnotenId)?.pfad ?? 'Mindmap-Punkt (nicht mehr vorhanden)') : null;
  const angebot = b.ausAngebotId ? s.belege.find((x) => x.id === b.ausAngebotId) : null;
  const rechnung = b.umgewandeltIn ? s.belege.find((x) => x.id === b.umgewandeltIn) : null;

  return (
    <div className="stapel">
      <div className="bu-betraege">
        <div>
          <span className="leise klein">{b.steuer.length ? 'Betrag brutto' : 'Betrag'}</span>
          <strong className="bu-gross">
            <Euro b={b} fokus />
          </strong>
          {fremd && <span className="leise klein">Original {betrag(b.betragCent, b.waehrung)}</span>}
        </div>
        {b.steuer.length > 0 && (
          <div>
            <span className="leise klein">netto</span>
            <strong className="bu-gross">
              <Euro b={b} netto fokus />
            </strong>
            <span className="leise klein">Steuer {betrag(b.umgerechnetSteuerCent, W)}</span>
          </div>
        )}
        {heuteKurs && (
          <div>
            <span className="leise klein">zum heutigen Kurs</span>
            <strong>{betrag(umrechnen(b.betragCent, heuteKurs), W)}</strong>
          </div>
        )}
      </div>
      {fremd && (
        <p className="leise klein bu-ohne-abstand">
          {b.kurs ? (
            <>
              Festgehaltener Kurs: {kursText(b.kurs, b.waehrung, W)}
              {b.kursDatum && ` vom ${datum(b.kursDatum)}`} – {KURS_QUELLE_NAMEN[b.kursQuelle]}.
            </>
          ) : (
            <span className="bu-text-warn">Noch kein Kurs bekannt – dieser Beleg zählt noch nicht zum Budget. Bitte bearbeiten und den Kurs von Hand eintragen.</span>
          )}
          {heuteKurs && (
            <>
              {' '}
              Heute: {kursText(heuteKurs, b.waehrung, W)}
              {kurse?.stand === 'ersatz' ? ' (letzter bekannter Kurs, Quelle nicht erreichbar)' : ''}.
            </>
          )}
        </p>
      )}
      {b.kursQuelle === 'ersatz' && (
        <Meldung art="info">Beim Speichern war die Kursquelle nicht erreichbar. Unter „Belege“ lassen sich die Kurse später nachholen.</Meldung>
      )}
      {b.steuer.length > 0 && (
        <div className="tabelle-rahmen">
          <table className="tabelle bu-steuer-ansicht">
            <thead>
              <tr>
                <th>Satz</th>
                <th className="bu-rechts">Netto</th>
                <th className="bu-rechts">Steuer</th>
                <th className="bu-rechts">Brutto</th>
              </tr>
            </thead>
            <tbody>
              {b.steuer.map((p, i) => (
                <tr key={i}>
                  <td>{satzText(p.satzBp)}</td>
                  <td className="bu-rechts">{betrag(p.nettoCent, b.waehrung)}</td>
                  <td className="bu-rechts">{betrag(p.steuerCent, b.waehrung)}</td>
                  <td className="bu-rechts">{betrag(p.nettoCent + p.steuerCent, b.waehrung)}</td>
                </tr>
              ))}
              {b.steuer.length > 1 && (
                <tr className="bu-tabelle-summe">
                  <td>Summe</td>
                  <td className="bu-rechts">{betrag(b.nettoCent, b.waehrung)}</td>
                  <td className="bu-rechts">{betrag(b.steuerCent, b.waehrung)}</td>
                  <td className="bu-rechts">{betrag(b.betragCent, b.waehrung)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <dl className="bu-daten">
        <dt>Art</dt>
        <dd>
          {b.art === 'rechnung' ? 'Rechnung (tatsächliche Kosten)' : b.art === 'angebot' ? 'Angebot (mögliche Kosten, noch keine Ausgabe)' : 'Einnahme'}
        </dd>
        <dt>Status</dt>
        <dd>
          <StatusMarke b={b} />
        </dd>
        <dt>Datum</dt>
        <dd>{b.datum ? datum(b.datum) : '–'}</dd>
        <dt>Von</dt>
        <dd>{b.absender || '–'}</dd>
        {b.art === 'einnahme' ? (
          <>
            <dt>Labels</dt>
            <dd>{b.labels.length ? <LabelMarken labels={b.labels} /> : <span className="leise">keine</span>}</dd>
          </>
        ) : (
          <>
            <dt>Kategorie</dt>
            <dd>{b.kategoriePfad ?? 'ohne Kategorie'}</dd>
          </>
        )}
        {!b.steuer.length && (
          <>
            <dt>Mehrwertsteuer</dt>
            <dd className="leise">nicht aufgeschlüsselt</dd>
          </>
        )}
        <dt>{WER_NAME[b.art]}</dt>
        <dd>
          {b.teilnehmerName ?? 'niemand zugeordnet'}
          {b.teilnehmerFrei && <span className="leise klein"> (ohne Konto)</span>}
        </dd>
        {knotenPfad && (
          <>
            <dt>Mindmap</dt>
            <dd>{knotenPfad}</dd>
          </>
        )}
        {b.notiz && (
          <>
            <dt>Notiz</dt>
            <dd className="bu-notiz">{b.notiz}</dd>
          </>
        )}
        {angebot && (
          <>
            <dt>Aus Angebot</dt>
            <dd>
              <button type="button" className="bu-link" onClick={() => oeffnen({ art: 'ansicht', id: angebot.id })}>
                {angebot.titel} ({betrag(angebot.umgerechnetCent, W)})
              </button>
            </dd>
          </>
        )}
        {rechnung && (
          <>
            <dt>Rechnung</dt>
            <dd>
              <button type="button" className="bu-link" onClick={() => oeffnen({ art: 'ansicht', id: rechnung.id })}>
                {rechnung.titel} ({betrag(rechnung.umgerechnetCent, W)})
              </button>
            </dd>
          </>
        )}
        <dt>Angelegt</dt>
        <dd className="leise">
          {datumZeit(b.erstelltAm)} · {b.quelleArt === 'hand' ? 'eingetragen' : `erkannt ${HERKUNFT[b.quelleArt]}`}
          {b.erstelltVonName ? ` von ${b.erstelltVonName}` : ''}
        </dd>
      </dl>
    </div>
  );
}

export function StatusMarke({ b }: { b: BelegAnsicht }) {
  const art =
    b.status === 'bezahlt' || b.status === 'erhalten'
      ? 'ok'
      : b.status === 'abgelehnt' || b.status === 'umgewandelt'
        ? 'leise'
        : b.art === 'angebot' || b.art === 'einnahme'
          ? 'akzent'
          : 'warn';
  return <span className={`bu-marke bu-marke-${art}`}>{statusName(b.art, b.status)}</span>;
}
